import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API } from '../App';
import axios from 'axios';
import LanguageToggle from '../components/LanguageToggle';
import { Heart, Upload, CheckCircle, AlertCircle, AlertTriangle, ShieldCheck, ArrowLeft, FileText, X } from 'lucide-react';

const ORG_TYPES = [
  { value: 'registered_charity', labelKey: 'charity.orgTypeCharity' },
  { value: 'non_profit', labelKey: 'charity.orgTypeNonProfit' },
  { value: 'community_group', labelKey: 'charity.orgTypeCommunity' },
  { value: 'volunteer_organisation', labelKey: 'charity.orgTypeVolunteer' },
];

const CharityApplication = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    organisationName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    organisationType: '',
    description: '',
    areaServed: '',
    registrationNumber: '',
    declaration: false,
  });
  const [proofFile, setProofFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  // Soft duplicate-organisation check (Iter 149).
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [duplicateChecking, setDuplicateChecking] = useState(false);
  // Iter 192 — Hard duplicate-guard 409 state. Surfaced inline after submit
  // when a near-duplicate already exists.
  const [duplicateBlock, setDuplicateBlock] = useState(null);

  // Debounced check against /api/businesses/check-duplicate when the user types
  // their organisation name. Same backend endpoint as the business signup flow —
  // charity listings live in the same `businesses` collection.
  useEffect(() => {
    const name = (form.organisationName || '').trim();
    if (name.length < 3) {
      setDuplicateMatches([]);
      return;
    }
    setDuplicateChecking(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/businesses/check-duplicate`, {
          params: { name },
        });
        setDuplicateMatches(res.data?.matches || []);
      } catch {
        setDuplicateMatches([]);
      } finally {
        setDuplicateChecking(false);
      }
    }, 450);
    return () => clearTimeout(handle);
  }, [form.organisationName]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError(t('charity.fileTooLarge'));
        return;
      }
      setProofFile(file);
      setError('');
    }
  };

  const handleSubmit = async (e, opts = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    setDuplicateBlock(null);

    if (!form.organisationName || !form.contactName || !form.email || !form.organisationType || !form.description || !form.areaServed) {
      setError(t('fillAllRequired'));
      return;
    }
    if (!form.declaration) {
      setError(t('charity.declarationRequired'));
      return;
    }
    if (!proofFile) {
      setError(t('charity.proofRequired'));
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        formData.append(key, value);
      });
      if (proofFile) {
        formData.append('proofFile', proofFile);
      }
      formData.append('confirmedNoDuplicate', opts.confirmedNoDuplicate ? 'true' : 'false');

      await axios.post(`${API}/charity/apply`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted(true);
    } catch (err) {
      // Iter 192 — surface 409 duplicate block inline for explicit retry.
      if (err.response?.status === 409 && err.response?.data?.detail?.error === 'duplicate_business') {
        setDuplicateBlock(err.response.data.detail);
      } else {
        setError(err.response?.data?.detail || t('charity.submitError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="charity-success-title">
            {t('charity.submittedTitle')}
          </h2>
          <p className="text-gray-600 mb-6">{t('charity.submittedDesc')}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            data-testid="charity-go-home-btn"
          >
            {t('goHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center mb-4">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="charity-back-btn">
              <ArrowLeft className="h-5 w-5" />
              {t('back')}
            </button>
            <LanguageToggle />
          </div>
          <div className="flex items-center gap-3">
            <Heart className="h-8 w-8" />
            <div>
              <h1 className="text-2xl font-bold" data-testid="charity-form-title">{t('charity.formTitle')}</h1>
              <p className="text-white/80 text-sm mt-1">{t('charity.formSubtitle')}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Eligibility Notice */}
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-5 mb-8">
          <h3 className="font-semibold text-teal-800 mb-2">{t('charity.eligibleTitle')}</h3>
          <ul className="text-sm text-teal-700 space-y-1 mb-3">
            <li>&#8226; {t('charity.eligibleItem1')}</li>
            <li>&#8226; {t('charity.eligibleItem2')}</li>
            <li>&#8226; {t('charity.eligibleItem3')}</li>
            <li>&#8226; {t('charity.eligibleItem4')}</li>
          </ul>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
            <p className="text-sm text-amber-800 font-medium">{t('charity.notForBusinesses')}</p>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="charity-error">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Organisation Details */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('charity.orgDetailsTitle')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('charity.organisationName')} *</label>
                <input
                  type="text" name="organisationName" value={form.organisationName} onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  placeholder={t('charity.organisationNamePlaceholder')}
                  data-testid="charity-org-name"
                />
                {/* Soft duplicate-organisation warning */}
                {duplicateMatches.length > 0 && (
                  <div
                    className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-4"
                    data-testid="charity-duplicate-warning"
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-amber-900 text-sm">
                          {t('charity.duplicateHeader', 'This organisation may already be listed.')}
                        </p>
                        <p className="text-xs text-amber-800/90 mt-0.5">
                          {t('charity.duplicateHelp2', 'A similar listing already exists.')}
                        </p>
                        <div className="mt-3 space-y-2">
                          {duplicateMatches.slice(0, 3).map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => navigate(`/business/${m.id}`)}
                              className="w-full flex items-center justify-between gap-3 rounded-lg bg-white border border-amber-300 px-3 py-2 text-left hover:bg-amber-100 transition-colors"
                              data-testid={`charity-duplicate-match-${m.id}`}
                            >
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-gray-900 truncate">{m.businessName}</span>
                                <span className="block text-xs text-gray-500 truncate">
                                  {[m.category, m.city].filter(Boolean).join(' — ')}
                                </span>
                              </span>
                              <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                                <ShieldCheck className="h-4 w-4" />
                                {t('charity.duplicateViewBtn', 'View listing')}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {duplicateChecking && duplicateMatches.length === 0 && form.organisationName.length >= 3 && (
                  <p className="mt-2 text-xs text-gray-400 italic" data-testid="charity-duplicate-checking">
                    {t('charity.duplicateChecking', 'Checking for existing listings…')}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('charity.contactName')} *</label>
                <input
                  type="text" name="contactName" value={form.contactName} onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  placeholder={t('charity.contactNamePlaceholder')}
                  data-testid="charity-contact-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('email')} *</label>
                <input
                  type="email" name="email" value={form.email} onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  placeholder="contact@organisation.org"
                  data-testid="charity-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('phoneNumber')}</label>
                <input
                  type="tel" name="phone" value={form.phone} onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  placeholder="+34 600 000 000"
                  data-testid="charity-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('charity.websiteSocial')}</label>
                <input
                  type="text" name="website" value={form.website} onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  placeholder="https://..."
                  data-testid="charity-website"
                />
              </div>
            </div>
          </div>

          {/* Organisation Type & Description */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('charity.aboutOrgTitle')}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('charity.orgType')} *</label>
                <select
                  name="organisationType" value={form.organisationType} onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white"
                  data-testid="charity-org-type"
                >
                  <option value="">{t('charity.selectOrgType')}</option>
                  {ORG_TYPES.map(o => (
                    <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('description')} *</label>
                <textarea
                  name="description" value={form.description} onChange={handleChange} rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none resize-none"
                  placeholder={t('charity.descriptionPlaceholder')}
                  data-testid="charity-description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('charity.areaServed')} *</label>
                <input
                  type="text" name="areaServed" value={form.areaServed} onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  placeholder={t('charity.areaServedPlaceholder')}
                  data-testid="charity-area-served"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('charity.registrationNumber')}</label>
                <input
                  type="text" name="registrationNumber" value={form.registrationNumber} onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  placeholder={t('charity.registrationNumberPlaceholder')}
                  data-testid="charity-reg-number"
                />
              </div>
            </div>
          </div>

          {/* Proof Upload */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('charity.proofTitle')} *</h2>
            <p className="text-sm text-gray-500 mb-4">{t('charity.proofDesc')}</p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-teal-400 transition-colors">
              {proofFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="h-6 w-6 text-teal-600" />
                  <span className="text-sm text-gray-700 font-medium">{proofFile.name}</span>
                  <button type="button" onClick={() => setProofFile(null)} className="p-1 hover:bg-gray-100 rounded-full">
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">{t('charity.proofUploadLabel')}</p>
                  <p className="text-xs text-gray-400">{t('charity.proofFileTypes')}</p>
                </>
              )}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileChange}
                className={proofFile ? 'hidden' : 'absolute inset-0 opacity-0 cursor-pointer'}
                style={proofFile ? {} : { position: 'relative' }}
                data-testid="charity-proof-upload"
              />
            </div>
          </div>

          {/* Declaration */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="charity-declaration-label">
              <input
                type="checkbox" name="declaration" checked={form.declaration} onChange={handleChange}
                className="mt-1 h-4 w-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                data-testid="charity-declaration"
              />
              <span className="text-sm text-gray-700">{t('charity.declarationText')}</span>
            </label>
          </div>

          {/* Iter 192 — Hard duplicate-organisation block (server-side 409 retry) */}
          {duplicateBlock && (
            <div
              className="rounded-xl border-2 border-red-300 bg-red-50 p-4"
              data-testid="charity-duplicate-block"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-red-900 text-sm">
                    {t('charity.duplicateBlockHeader', 'A similar organisation is already listed')}
                  </p>
                  <p className="text-xs text-red-800/90 mt-1">
                    {t('charity.duplicateBlockHelp2', 'A similar listing already exists. If this is genuinely a different organisation, you can confirm and continue.')}
                  </p>
                  {duplicateBlock.match && (
                    <button
                      type="button"
                      onClick={() => navigate(`/business/${duplicateBlock.match.id}`)}
                      className="mt-3 w-full flex items-center justify-between gap-3 rounded-lg bg-white border border-red-300 px-3 py-2 text-left hover:bg-red-100 transition-colors"
                      data-testid={`charity-duplicate-block-match-${duplicateBlock.match.id}`}
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-gray-900 truncate">{duplicateBlock.match.businessName}</span>
                        <span className="block text-xs text-gray-500 truncate">
                          {[duplicateBlock.match.category, duplicateBlock.match.city].filter(Boolean).join(' — ')}
                        </span>
                      </span>
                      <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                        <ShieldCheck className="h-4 w-4" />
                        {t('duplicateViewBtn', 'View listing')}
                      </span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(ev) => handleSubmit(ev, { confirmedNoDuplicate: true })}
                    disabled={submitting}
                    className="mt-3 w-full py-2.5 bg-white border-2 border-red-400 text-red-800 font-medium rounded-lg hover:bg-red-100 transition-colors text-sm disabled:opacity-50"
                    data-testid="charity-duplicate-block-confirm-different"
                  >
                    {submitting ? t('submitting') : t('charity.duplicateBlockConfirmDifferent', 'This is a different organisation — submit anyway')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              data-testid="charity-cancel-btn"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="charity-submit-btn"
            >
              {submitting ? (
                <>{t('submitting')}</>
              ) : (
                <>
                  <Heart className="h-4 w-4" />
                  {t('charity.submitApplication')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CharityApplication;
